<script setup>
import imgCarDrive from '@/assets/static/images/arrival/arrival-car-drive.webp';
import imgCarParked from '@/assets/static/images/arrival/arrival-car-parked.webp';
import imgPhoneBell from '@/assets/static/images/arrival/arrival-phone-bell.webp';
import imgFuelPump from '@/assets/static/images/arrival/arrival-fuel-pump.webp';

const props = defineProps({ data: { type: Object, required: true } });
const emits = defineEmits(['close']);

const fnEnable = () => {
  emits('close');
  props.data.onEnable();
};

const fnLater = () => {
  emits('close');
  props.data.onLater();
};

const fnBackdrop = () => {
  if (props.data.mode !== 'offer') emits('close');
};
</script>

<template>
  <div class="modal-dim on ag-dim">
    <!-- 부모의 공용 바깥 클릭 처리가 offer를 닫거나 방금 열린 결과 창을 지우지 않도록 전파를 막는다. -->
    <div class="ag-sheet-wrap" @click.stop.self="fnBackdrop">
      <div class="ag-sheet modal" role="dialog" aria-labelledby="ag-sheet-title">
        <div class="ag-sheet__body">
          <template v-if="data.mode === 'offer'">
            <div class="ag-story">
              <figure class="ag-story__step">
                <img class="ag-story__image" :src="imgCarDrive" width="384" height="384" alt="">
                <figcaption class="ag-story__caption">차로 이동</figcaption>
              </figure>
              <svg class="ag-icon ag-story__arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M5 12h14m-6-6 6 6-6 6" />
              </svg>
              <figure class="ag-story__step">
                <img class="ag-story__image" :src="imgCarParked" width="384" height="384" alt="">
                <figcaption class="ag-story__caption">차에서 내리면</figcaption>
              </figure>
              <svg class="ag-icon ag-story__arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M5 12h14m-6-6 6 6-6 6" />
              </svg>
              <figure class="ag-story__step">
                <img class="ag-story__image" :src="imgPhoneBell" width="384" height="384" alt="">
                <figcaption class="ag-story__caption">바로 알림</figcaption>
              </figure>
            </div>
            <h2 id="ag-sheet-title" class="ag-sheet__title">주차 기록 알림을 켜 두시겠습니까?</h2>
            <div class="ag-card ag-features">
              <div class="ag-feature">
                <svg class="ag-icon ag-feature__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
                </svg>
                <p class="ag-feature__text">차에서 내리시면 '주차하셨습니까?' 알림이 옵니다.</p>
              </div>
              <div class="ag-feature">
                <svg class="ag-icon ag-feature__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="m8 5 2-2h4l2 2h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4Z" />
                  <circle cx="12" cy="12" r="4" />
                </svg>
                <p class="ag-feature__text">알림을 누르시면 주차 위치와 기둥 사진을 바로 남길 수 있습니다.</p>
              </div>
            </div>
            <p class="ag-note">주차 QR로 오는 연락 알림과는 별개의 알림입니다.</p>
          </template>

          <template v-else-if="data.mode === 'done'">
            <div class="ag-result">
              <img class="ag-result__image" :src="imgPhoneBell" width="384" height="384" alt="">
              <span class="ag-result__check" aria-hidden="true">
                <svg class="ag-icon" viewBox="0 0 24 24" focusable="false">
                  <path d="m5 12 4 4L19 6" />
                </svg>
              </span>
            </div>
            <h2 id="ag-sheet-title" class="ag-sheet__title">주차 기록 알림을 켰습니다</h2>
            <div v-if="data.fuel" class="ag-card ag-fuel">
              <img class="ag-fuel__image" :src="imgFuelPump" width="384" height="384" alt="">
              <p class="ag-fuel__text">주유 기록 알림도 켜져 있습니다.</p>
            </div>
            <div class="ag-card ag-settings">
              <p class="ag-settings__label">끄실 때는</p>
              <div class="ag-settings__route">
                <span class="ag-settings__pill">내 등록차량 보기</span>
                <svg class="ag-icon ag-settings__arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M5 12h14m-6-6 6 6-6 6" />
                </svg>
                <span class="ag-settings__pill">주차 기록 알림 끄기</span>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="ag-status ag-status--notice" aria-hidden="true">
              <svg class="ag-icon" viewBox="0 0 24 24" focusable="false">
                <path d="M12 5v9m0 4h.01" />
              </svg>
            </div>
            <h2 id="ag-sheet-title" class="ag-sheet__title">주차 기록 알림이 꺼져 있습니다</h2>
            <p class="ag-card ag-error">{{ data.error }}</p>
            <p class="ag-note">'내 등록차량 보기'에서 언제든 다시 켤 수 있습니다.</p>
          </template>
        </div>

        <div class="ag-sheet__actions">
          <template v-if="data.mode === 'offer'">
            <button type="button" class="ag-button ag-button--secondary" @click.stop="fnLater">나중에</button>
            <button type="button" class="ag-button ag-button--primary" @click.stop="fnEnable">켜기</button>
          </template>
          <button v-else type="button" class="ag-button ag-button--primary" @click.stop="emits('close')">확인</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 전역 dim(20)·홈 머리띠(40)뿐 아니라 아래 메뉴·떠 있는 동그라미 단추(TheNav .nav 9999)보다 위 —
   50 이던 첫 판은 교회 홈의 '내 등록차량 보기'·'간판ON 둘러보기' 동그라미가 시트 글을 덮었다(261104 화면 확인). */
.ag-dim { z-index: 10000; word-break: keep-all; overflow-wrap: break-word; }
.ag-sheet-wrap { position: absolute; inset: 0; z-index: 1; display: flex; align-items: flex-end; justify-content: center; }
.ag-sheet.modal {
  --ag-navy: #0F1F3D;
  --ag-primary: #306DF7;
  --ag-blue: #3182F6;
  --ag-ice: #EEF3FF;
  --ag-wash: #F7F8FA;
  --ag-white: #fff;
  --ag-ink: #1F212A;
  --ag-body: #3D3F4D;
  --ag-line: #E5E7EB;
  --ag-notice: #94600E;
  --ag-notice-wash: #FFF2D8;
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%; min-width: 0; max-width: 600px; max-height: 90%;
  overflow: hidden;
  border-radius: 24px 24px 0 0;
  background: var(--ag-wash);
  box-shadow: 0 -8px 32px rgba(15, 31, 61, .12);
  color: var(--ag-ink);
  font-family: 'Pretendard', system-ui, -apple-system, sans-serif;
  font-size: 16px; line-height: 1.5; letter-spacing: -0.02em; text-align: left;
  word-break: keep-all; overflow-wrap: break-word;
  animation: ag-sheet-up .3s ease;
}
.ag-sheet__body { min-height: 0; padding: 24px 20px 20px; overflow-y: auto; overscroll-behavior-y: contain; }
.ag-sheet__title { margin: 0 0 16px; color: var(--ag-navy); font-size: 24px; font-weight: 800; line-height: 1.35; letter-spacing: -0.03em; }
.ag-icon { display: block; width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.ag-story { display: grid; grid-template-columns: minmax(0, 1fr) 16px minmax(0, 1fr) 16px minmax(0, 1fr); align-items: start; gap: 4px; margin-bottom: 20px; }
.ag-story__step { min-width: 0; margin: 0; text-align: center; }
.ag-story__image { display: block; width: 100%; max-width: 96px; height: auto; aspect-ratio: 1; margin: 0 auto 4px; object-fit: contain; }
.ag-story__caption { color: var(--ag-body); font-size: 16px; font-weight: 600; line-height: 1.5; }
.ag-story__arrow { width: 16px; height: 16px; margin-top: 40px; color: var(--ag-primary); }
.ag-card { box-sizing: border-box; padding: 16px; border-radius: 16px; background: var(--ag-white); }
.ag-features { display: flex; flex-direction: column; gap: 12px; }
.ag-feature { display: flex; align-items: flex-start; gap: 12px; }
.ag-feature__icon { flex: 0 0 24px; color: var(--ag-primary); }
.ag-feature__text { min-width: 0; margin: 0; color: var(--ag-body); font-size: 16px; line-height: 1.5; }
.ag-note { margin: 16px 0 0; padding: 12px 16px; border-radius: 16px; background: var(--ag-ice); color: var(--ag-navy); font-size: 16px; line-height: 1.5; }
.ag-result { position: relative; width: 112px; margin: 0 auto 20px; }
.ag-result__image { display: block; width: 112px; height: 112px; object-fit: contain; }
.ag-result__check { position: absolute; right: 0; bottom: 0; display: flex; align-items: center; justify-content: center; box-sizing: border-box; width: 36px; height: 36px; border: 3px solid var(--ag-wash); border-radius: 50%; background: var(--ag-primary); color: var(--ag-white); }
.ag-fuel { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.ag-fuel__image { flex: 0 0 64px; width: 64px; height: 64px; object-fit: contain; }
.ag-fuel__text { min-width: 0; margin: 0; color: var(--ag-ink); font-size: 16px; font-weight: 600; line-height: 1.5; }
.ag-settings__label { margin: 0 0 12px; color: var(--ag-body); font-size: 16px; line-height: 1.5; }
.ag-settings__route { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.ag-settings__pill { box-sizing: border-box; max-width: 100%; padding: 8px 14px; border-radius: 999px; background: var(--ag-ice); color: var(--ag-navy); font-size: 16px; font-weight: 700; line-height: 1.5; }
.ag-settings__arrow { width: 20px; height: 20px; margin-left: 16px; color: var(--ag-primary); transform: rotate(90deg); }
.ag-status { display: flex; align-items: center; justify-content: center; width: 72px; height: 72px; margin: 0 auto 20px; border-radius: 50%; }
.ag-status .ag-icon { width: 36px; height: 36px; }
.ag-error { margin: 0; color: var(--ag-body); font-size: 16px; line-height: 1.5; white-space: pre-wrap; }
.ag-sheet__actions { display: flex; flex: 0 0 auto; gap: 12px; padding: 0 20px calc(16px + env(safe-area-inset-bottom)); }
.ag-button { appearance: none; display: flex; flex: 1 1 0; align-items: center; justify-content: center; box-sizing: border-box; width: 100%; min-width: 0; min-height: 56px; margin: 0; padding: 12px 16px; border: 1px solid transparent; border-radius: 12px; font-family: inherit; font-size: 18px; font-weight: 700; line-height: 1.5; letter-spacing: inherit; white-space: normal; cursor: pointer; touch-action: manipulation; }
.ag-button:focus-visible { outline: 3px solid var(--ag-blue); outline-offset: 3px; }
.ag-button:active { filter: brightness(.9); }

@keyframes ag-sheet-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
@media (max-width: 374px) {
  .ag-sheet__body { padding-right: 16px; padding-left: 16px; }
  .ag-sheet__actions { padding-right: 16px; padding-left: 16px; }
}
@media (min-width: 480px) {
  .ag-settings__route { flex-direction: row; align-items: center; }
  .ag-settings__arrow { flex: 0 0 20px; margin-left: 0; transform: none; }
}
@media (prefers-reduced-motion: reduce) { .ag-sheet.modal { animation: none; } }

.ag-button--primary { background: var(--ag-primary); color: var(--ag-white); }
.ag-button--secondary { border-color: var(--ag-line); background: #ECEEF2; color: var(--ag-body); }
.ag-status--notice { background: var(--ag-notice-wash); color: var(--ag-notice); }
</style>
